<template>
  <div>
    <ComboBox
      class="l-PersonalMessageTemplateSelector"
      :options="messages"
      :canClear="false"
      labelProp="name"
      valueProp="id"
      :placeholder="$t('createEnvelope.personalMessage.selector.placeholder')"
      :noOptions="$t('createEnvelope.personalMessage.selector.noOptions')"
      :noResults="$t('createEnvelope.personalMessage.selector.noResults')"
      @select="handleSelect"
      data-hj-suppress
      data-ec="envelope_send"
      data-et="party_personal_message_dropdown"
    >
      <template
        v-if="userHasRole('super-admin')"
        #action="{ option }"
      >
        <BaseButton
          variant="invisible"
          size="medium"
          class="whitespace-nowrap !py-sm !px-base hover:!text-dark3 !no-underline hover:!underline"
          @click.native="openModal($event, option)"
          @keydown.native.enter="openModal($event, option)"
          data-ec="envelope_send"
          data-et="party_personal_message_open_delete_modal"
        >
          {{ $t('functional.delete') }}
        </BaseButton>
      </template>
    </ComboBox>

    <TWModal
      v-if="userHasRole('super-admin')"
      :title="modalTitle"
      :show="modal.show"
      :loading="false"
      @close="closeModal"
    >
      <template v-slot:content>
        <p class="whitespace-pre-line">{{ modalContent }}</p>
      </template>
      <template v-slot:controls>
        <BaseButton
          variant="invisible"
          class="hover:!text-dark3"
          @click.native="closeModal"
          data-ec="envelope_send"
          data-et="party_personal_message_close_delete_modal"
        >
          {{ $t('functional.cancel') }}
        </BaseButton>
        <BaseButton
          variant="danger"
          @click.native="handleDelete"
          data-ec="envelope_send"
          data-et="party_personal_message_delete"
        >
          {{ $t('functional.permanentlyDelete') }}
        </BaseButton>
      </template>
    </TWModal>
  </div>
</template>

<script setup>
// @ts-check
import { computed, onMounted, ref } from 'vue';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import i18n from '@/locales';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import ComboBox from '@/components/tailwind/forms/ComboBox';
import TWModal from '@/components/tailwind/Modal.vue';

const emit = defineEmits(['select']);

const { messages, userHasRole } = useGetters({
  messages: 'company/messages',
  userHasRole: 'roles/userHasRole',
});
const { getMessages, deleteMessage } = useActions({
  getMessages: 'company/GET_MESSAGES',
  deleteMessage: 'company/DELETE_MESSAGE',
});

onMounted(() => getMessages());

const initialModal = {
  id: null,
  name: null,
  show: false,
};
const modal = ref(initialModal);

/**
 * @param {Event} event
 * @param {object} option
 * @param {number} option.id
 * @param {string} option.name
 */
function openModal(event, option) {
  event.preventDefault();
  event.stopPropagation();

  modal.value = {
    id: option.id,
    name: option.name,
    show: true,
  };
}
function closeModal() {
  modal.value = initialModal;
}

/** @param {number} value */
function handleSelect(value) {
  emit(
    'select',
    messages.value.find(message => message.id === value),
  );
}

async function handleDelete() {
  await deleteMessage(modal.value);

  closeModal();
}

const modalTitle = computed(() =>
  i18n.t('createEnvelope.personalMessage.deleteModal.title', {
    name: modal.value.name,
  }));
const modalContent = computed(() =>
  i18n.t('createEnvelope.personalMessage.deleteModal.description'));
</script>
